import React from "react";
import {
  Zap,
  Workflow,
  MessagesSquare,
  Magnet,
  Brain,
  HeartHandshake,
  GaugeCircle,
} from "lucide-react";

const needs = [
  {
    icon: Zap,
    title: "Fast response times",
    desc: "Speed-to-lead defines conversion. Replies in seconds — not hours.",
    metric: "< 30s",
  },
  {
    icon: Workflow,
    title: "Automation",
    desc: "Eliminate repetitive ops. Free up teams for higher-leverage work.",
    metric: "200+ flows",
  },
  {
    icon: MessagesSquare,
    title: "Modern communication",
    desc: "Voice, SMS, email, chat — unified and on-brand at every touchpoint.",
    metric: "Omnichannel",
  },
  {
    icon: Magnet,
    title: "Lead capture",
    desc: "Never miss inbound. Capture, qualify, and route every opportunity.",
    metric: "100% inbound",
  },
  {
    icon: Brain,
    title: "AI systems",
    desc: "Agents and copilots embedded into operations — not bolted on.",
    metric: "Production-grade",
  },
  {
    icon: HeartHandshake,
    title: "Customer experience",
    desc: "Premium, fast, and consistent — at any scale, in any channel.",
    metric: "5-star UX",
  },
  {
    icon: GaugeCircle,
    title: "Operational efficiency",
    desc: "Lower cost-per-lead, lower CAC, higher throughput per employee.",
    metric: "↑ Margin",
  },
];

export default function BusinessNeeds() {
  return (
    <section
      id="needs"
      className="relative section-padding"
      data-testid="needs-section"
    >
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 mb-14 reveal">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-[#39FF14]">
              <span className="w-6 h-px bg-[#39FF14]" />
              Built For Now
            </div>
            <h2 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white">
              What modern businesses{" "}
              <span className="gradient-text-green">actually need.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-6">
            <p className="text-zinc-400 text-lg leading-relaxed">
              The bar has shifted. Customers expect instant responses, frictionless
              booking, and intelligent conversations — on every channel, around
              the clock. We build the systems that meet that expectation.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 reveal">
          {needs.map((n, i) => (
            <div
              key={n.title}
              className="group p-7 rounded-2xl border border-white/5 bg-[#0A0A0A] hover:border-[#39FF14]/25 transition-all"
              data-testid={`need-${i + 1}`}
            >
              <div className="flex items-center justify-between">
                <div className="svc-icon">
                  <n.icon className="w-5 h-5" />
                </div>
                <span className="text-[10px] font-mono uppercase tracking-widest text-[#39FF14]/80 px-2.5 py-1 rounded-full border border-[#39FF14]/20">
                  {n.metric}
                </span>
              </div>
              <h3 className="mt-5 text-xl font-semibold text-white">
                {n.title}
              </h3>
              <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                {n.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
